import React, { useEffect, useRef, useState } from "react";
import "../../css/reels.css";
import { getActiveReels } from "../../services/reelsServices";
import { mediaUrl } from "../../utils/mediaUrl";

// if there's no poster image, seek a hair into the video so we don't
// land on a black/blank opening frame
const handleLoadedMetadata = (e, hasThumbnail) => {
  if (hasThumbnail) return;
  try {
    e.currentTarget.currentTime = Math.min(0.3, (e.currentTarget.duration || 1) / 4);
  } catch (err) {
    // ignore — some browsers throw if called too early
  }
};

const AUTO_SLIDE_INTERVAL = 3000; // ms between auto-advances

const ReelItem = ({ reel }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const hasThumbnail = Boolean(reel.thumbnail);

  const togglePlay = (e) => {
    e.stopPropagation();
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) {
      vid.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      vid.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setIsMuted(vid.muted);
  };

  return (
    <div
      className="reel-card"
      onClick={togglePlay}
      role="button"
      tabIndex={0}
      aria-label="Toggle reel play"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          togglePlay(e);
        }
      }}
    >
      <video
        ref={videoRef}
        className="reel-media"
        src={mediaUrl(reel.video)}
        poster={hasThumbnail ? mediaUrl(reel.thumbnail) : undefined}
        preload="metadata"
        autoPlay
        muted={isMuted}
        loop
        playsInline
        onLoadedMetadata={(e) => {
          handleLoadedMetadata(e, hasThumbnail);
          e.currentTarget.play().catch(() => {});
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      <button
        type="button"
        className="reel-sound-btn"
        onClick={toggleMute}
        aria-label={isMuted ? "Unmute reel" : "Mute reel"}
      >
        {isMuted ? "🔇" : "🔊"}
      </button>

      {!isPlaying && (
        <div className="reel-play-overlay" aria-hidden="true">
          <span>▶</span>
        </div>
      )}
    </div>
  );
};

const Reels = () => {
  const sliderRef = useRef(null);
  const isHoveringRef = useRef(false);
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const data = await getActiveReels();
        if (isMounted) setReels(data);
      } catch (err) {
        console.error(err);
        if (isMounted) setError("Failed to load reels");
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const scroll = (direction) => {
    if (!sliderRef.current) return;
    const card = sliderRef.current.querySelector(".reel-card");
    const scrollAmount = card ? card.offsetWidth + 16 : 300;
    sliderRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  // auto-advance the slider, looping back to the start at the end
  useEffect(() => {
    if (reels.length < 2) return;

    const timer = setInterval(() => {
      const slider = sliderRef.current;
      if (!slider || isHoveringRef.current) return;

      const atEnd = slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 4;

      if (atEnd) {
        slider.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        scroll("right");
      }
    }, AUTO_SLIDE_INTERVAL);

    return () => clearInterval(timer);
  }, [reels]);

  if (loading || error || reels.length === 0) {
    // render nothing on the storefront if there's no data / an error,
    // so the section doesn't show a broken empty slider
    return null;
  }

  return (
    <section className="reels-section">
      <h2 className="reels-title">Styled by our customers</h2>

      <div className="reels-slider-wrapper">
        <button
          className="reels-arrow reels-arrow-left"
          onClick={() => scroll("left")}
          aria-label="Scroll left"
        >
          &#8592;
        </button>

        <div
          className="reels-slider"
          ref={sliderRef}
          onMouseEnter={() => { isHoveringRef.current = true; }}
          onMouseLeave={() => { isHoveringRef.current = false; }}
        >
          {reels.map((reel) => (
            <ReelItem key={reel._id} reel={reel} />
          ))}
        </div>

        <button
          className="reels-arrow reels-arrow-right"
          onClick={() => scroll("right")}
          aria-label="Scroll right"
        >
          &#8594;
        </button>
      </div>
    </section>
  );
};

export default Reels;
